/* istanbul ignore file */
import MaskedInput from 'react-text-mask';
import { getBaseInputLayoutProps, getCommonInputProps } from './BaseInputLayout';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import type { ChangeEventType, CurrencyInputLayoutProps } from '../../../../form/input/currencyInputTypes';
import { Box } from '@mui/material';
import { useUiTheme } from 'framework/uiTheme';

const defaultMaskOptions = {
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowDecimal: true,
  decimalSymbol: '.',
  allowNegative: false,
  allowLeadingZeroes: false,
};

const adornmentSx = {
  position: 'absolute',
  top: '9px',
  right: '12px',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none',
};

const CurrencyInputLayout = ({
  decimalLimit = 2,
  integerLimit,
  allowNegative = false,
  allowDecimal = true,
  onChangeCallback,
  placeholder: inputPlaceholder,
  autocomplete,
  suffix,
  endAdornment,
  ...rest
}: CurrencyInputLayoutProps) => {
  const { BaseInputLayout } = useUiTheme();

  const maskOptions = {
    decimalLimit,
    integerLimit,
    allowNegative,
    allowDecimal,
  };

  const placeholder = inputPlaceholder || (decimalLimit ? `0.${'0'.repeat(decimalLimit)}` : '0');

  const currencyMask = createNumberMask({
    ...defaultMaskOptions,
    ...maskOptions,
  });

  const suffixLength = (suffix?.length as number) || 0;
  const inputPaddingRight = endAdornment ? '48px' : `${12 + suffixLength * 9}px`;

  return (
    <BaseInputLayout {...getBaseInputLayoutProps(rest)} shouldBeShrinkedByDefault>
      <Box sx={{ position: 'relative' }}>
        {(suffix || endAdornment) && (
          <Box
            sx={{
              ...adornmentSx,

              opacity: rest.disabled ? 0.38 : 1,
              '& + input': {
                paddingRight: inputPaddingRight,
              },
            }}
          >
            {endAdornment}
            {suffix}
          </Box>
        )}
        <MaskedInput
          {...getCommonInputProps(rest)}
          mask={currencyMask}
          onChange={onChangeCallback}
          placeholder={placeholder}
          // onChange doesn't receive the event
          // so instead of using onChange we need to use onInput
          // https://github.com/text-mask/text-mask/pull/993
          onInput={(e: ChangeEventType) => {
            if (!(e.target as HTMLInputElement).value) {
              onChangeCallback(e);
            }
          }}
          autoComplete={autocomplete}
        />
      </Box>
    </BaseInputLayout>
  );
};

export default CurrencyInputLayout;
